import React, { useState, useEffect, useRef } from "react";
import { pinyin } from "pinyin-pro";
// @ts-ignore
import { Scrollbars } from "@om-tlh/react-custom-scrollbars";
import "./CharacterSequence.less";

interface CharacterSequenceProps {
  text: string;
  showPinyin?: boolean;
  inputtedChars: string;
  onInit?: (keys: CharState[]) => void; // 初始化时调用
  onCurrentCharChange?: (index: number, chars: CharState[]) => void;
  onComplete?: () => void;
  maxWidth?: number; // 最大宽度，用于自动换行
  textAlign?: "left" | "center" | "right"; // 对齐方式
}

interface CharState {
  char: string;
  isPunctuation: boolean;
  status: "untyped" | "correct" | "incorrect";
  pinyin?: string;
  translate?: string;
  keys?: string; // 拼音键位
  row: number; // 所在行
  col: number; // 所在列
  isNewLine: boolean; // 是否为换行符
}

// 判断是否为标点符号
const isPunctuation = (char: string) => {
  const punctuationRegex =
    /[\u3000-\u303F\uFF00-\uFF60\u0020-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u007E]/;
  return punctuationRegex.test(char);
};

const CharacterSequence: React.FC<CharacterSequenceProps> = ({
  text,
  showPinyin = false,
  inputtedChars,
  onInit,
  onCurrentCharChange,
  onComplete,
  maxWidth = 0, // 最大宽度，0表示不限制
  textAlign = "left", // 默认左对齐
}) => {
  const [initStates, setInitStates] = useState<CharState[]>([]);
  const [charStates, setCharStates] = useState<CharState[]>([]);
  const containerRef = useRef<any>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const hasCompletedRef = useRef(false);

  // 处理换行和自动换行逻辑
  useEffect(() => {
    if (!containerWidth) return;
    if (!text) return;

    const width = maxWidth || containerWidth;

    // 每行字符数（考虑间距）
    const charWidth = 58; // 每个卡片宽度+间距
    const charsPerRow = Math.max(1, Math.floor(width / charWidth));

    // 处理文本中的换行符
    const splitIndex = text.lastIndexOf("###");
    const lines = (splitIndex > 0 ? text.substring(0, splitIndex) : text).split(
      "\n"
    );
    const tranLines = text.substring(splitIndex + 3).split("\n");
    let newCharStates: CharState[] = [];
    let currentRow = 0;
    let currentCol = 0;

    lines.forEach((line, lineIndex) => {
      const tranLine = tranLines[lineIndex] || '';
      let tranWordIndex = 0;
      let tranWordEnd = (tranLine + " ").indexOf(" ", tranWordIndex + 1);
      let nextOk = false;
      // 处理当前行的字符
      Array.from(line).forEach((char, charIndex) => {
        if (isPunctuation(char)) {
          nextOk = true;
          tranWordIndex = (tranLine + " ").indexOf(" ", tranWordIndex);
          tranWordEnd = (tranLine + " ").indexOf(" ", tranWordIndex + 1);
        }
        if (nextOk && !isPunctuation(char)) {
          nextOk = false;
        }
        newCharStates.push({
          char,
          isPunctuation: isPunctuation(char),
          status: "untyped",
          pinyin: splitIndex > 0 ? "" : pinyin(char),
          translate:
            tranWordIndex < tranWordEnd
              ? tranLine.substring(tranWordIndex, tranWordIndex + 1)
              : " ",
          keys: pinyin(char, {
            toneType: "none",
          }),
          row: currentRow,
          col: currentCol,
          isNewLine: false,
        });

        currentCol++;
        tranWordIndex < tranWordEnd && tranWordIndex++;

        // 自动换行判断
        if (currentCol >= charsPerRow) {
          currentRow++;
          currentCol = 0;
        }
      });

      // 如果不是最后一行，添加换行
      if (lineIndex < lines.length - 1) {
        currentRow++;
        currentCol = 0;
      }
    });

    setInitStates(newCharStates);
    onInit?.(newCharStates);
    hasCompletedRef.current = false;
  }, [text, onInit, maxWidth, containerWidth]);

  useEffect(() => {
    if (!initStates.length) return;

    const newStates = [...initStates];
    let inputIndex = 0;
    let currentIndex = -1;
    let hasRemaining = false;

    // 更新每个字符的状态
    for (let i = 0; i < newStates.length; i++) {
      const charState = newStates[i];

      // if (charState.isPunctuation) {
      //   charState.status = "correct";
      // } else {
      if (inputIndex < inputtedChars.length) {
        charState.status =
          inputtedChars[inputIndex] === charState.char
            ? "correct"
            : "incorrect";
        inputIndex++;
      } else {
        charState.status = "untyped";
        if (currentIndex === -1) {
          currentIndex = i;
        }
        hasRemaining = true;
      }
      // }
    }

    setCharStates(newStates);

    if (currentIndex !== -1) {
      onCurrentCharChange?.(currentIndex, newStates);
      hasCompletedRef.current = false;
    } else if (!hasRemaining) {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onComplete?.();
      }
    }
  }, [inputtedChars, initStates, onCurrentCharChange, onComplete]);

  // 处理窗口大小变化
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.getClientWidth());
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.getClientWidth());
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [containerRef]);

  return (
    <Scrollbars
      autoHide
      ref={containerRef}
      className="character-sequence-container"
    >
      <div
        className="character-grid"
        style={{
          justifyContent: textAlign,
        }}
      >
        {charStates.map((charState, index) => (
          <div
            key={index}
            className={`character-card ${
              charState.status === "untyped" &&
              charStates.slice(0, index).every((c) => c.status !== "untyped")
                ? "current"
                : ""
            } ${
              charState.status === "correct"
                ? "correct"
                : charState.status === "incorrect"
                ? "incorrect"
                : ""
            }`}
          >
            {showPinyin &&
              (charState.pinyin || charState.translate) &&
              !charState.isPunctuation &&
              !charState.isNewLine && (
                <div className="pinyin">
                  {charState.pinyin || charState.translate}
                </div>
              )}
            <div className="char">{charState.char}</div>
          </div>
        ))}
      </div>
    </Scrollbars>
  );
};

export default CharacterSequence;
